import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Trash2, Eye, GitMerge, CheckCircle2 } from "lucide-react";

export function DuplicatesModal({ open, onClose, groups, onMerge, onDelete, onNavigate }) {
  const [keepSelection, setKeepSelection] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKeepSelection(prev => {
      const next = { ...prev };
      groups.forEach(g => {
        if (!next[g.key] || !g.orders.some(o => o.order_id === next[g.key])) {
          next[g.key] = g.orders[0]?.order_id;
        }
      });
      return next;
    });
  }, [open, groups]);

  const getField = (o, ...keys) => {
    const ed = o.extracted_data || {};
    for (const k of keys) {
      const v = ed[k];
      if (v !== null && v !== undefined && v !== "") return String(v);
    }
    return "";
  };

  const handleMerge = async (group) => {
    const keepId = keepSelection[group.key];
    if (!keepId) return;
    const mergeIds = group.orders.filter(o => o.order_id !== keepId).map(o => o.order_id);
    if (mergeIds.length === 0) return;
    if (!window.confirm(`Fusionner ${mergeIds.length} commande(s) dans la commande conservée ?\n\nLes champs manquants seront complétés, les pièces jointes, emails et commentaires seront transférés, puis les doublons supprimés.`)) return;
    setBusy(true);
    await onMerge(keepId, mergeIds);
    setBusy(false);
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm("Supprimer cette commande en double ?\n\nCette action est irréversible.")) return;
    setBusy(true);
    await onDelete(orderId);
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="duplicates-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-amber-500" />
            Commandes en double
            <span className="text-sm font-normal text-zinc-500">(même client, même date)</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 pr-1">
          {groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-zinc-500 gap-2" data-testid="no-duplicates-message">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
              <p className="text-sm">Aucun doublon détecté</p>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.key} className="border border-amber-200 bg-amber-50/40 rounded-lg p-4" data-testid={`duplicate-group-${group.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-zinc-800 text-sm">
                    {group.name} <span className="text-zinc-500 font-normal">— {group.date}</span>
                    <span className="ml-2 px-2 py-0.5 bg-amber-200 text-amber-800 text-xs rounded-full">{group.orders.length} commandes</span>
                  </h3>
                  <Button
                    size="sm"
                    onClick={() => handleMerge(group)}
                    disabled={busy}
                    className="gap-1.5 bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid={`merge-group-btn-${group.key}`}
                  >
                    <GitMerge className="w-4 h-4" />
                    Fusionner
                  </Button>
                </div>
                <div className="space-y-2">
                  {group.orders.map((o) => (
                    <div key={o.order_id} className={`flex items-center gap-3 p-2.5 rounded-md border text-sm ${keepSelection[group.key] === o.order_id ? 'border-green-400 bg-green-50' : 'border-zinc-200 bg-white'}`}>
                      <label className="flex items-center gap-2 cursor-pointer flex-shrink-0" title="Commande à conserver">
                        <input
                          type="radio"
                          name={`keep-${group.key}`}
                          checked={keepSelection[group.key] === o.order_id}
                          onChange={() => setKeepSelection(prev => ({ ...prev, [group.key]: o.order_id }))}
                          className="accent-green-600"
                          data-testid={`keep-radio-${o.order_id}`}
                        />
                        <span className={`text-xs font-medium ${keepSelection[group.key] === o.order_id ? 'text-green-700' : 'text-zinc-400'}`}>Conserver</span>
                      </label>
                      <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-0.5">
                        <span className="truncate"><span className="text-zinc-400 text-xs">Type :</span> {getField(o, "TYPE", "type") || "—"}</span>
                        <span className="truncate"><span className="text-zinc-400 text-xs">Statut :</span> {getField(o, "ORDER STATUS", "order_status") || "—"}</span>
                        <span className="truncate"><span className="text-zinc-400 text-xs">Montant :</span> {getField(o, "REVENUE TAX INCLUDED", "revenue_tax_included") || "—"}</span>
                        <span className="truncate text-zinc-500 text-xs self-center">Créée : {o.created_at ? new Date(o.created_at).toLocaleDateString("fr-FR") : "—"}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => onNavigate(o.order_id)} className="p-1.5 hover:bg-blue-100 rounded text-blue-600" title="Voir dans le tableau" data-testid={`view-duplicate-${o.order_id}`}>
                          <Eye className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(o.order_id)} disabled={busy} className="p-1.5 hover:bg-red-100 rounded text-red-500" title="Supprimer cette commande" data-testid={`delete-duplicate-${o.order_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end pt-2 border-t border-zinc-100">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="close-duplicates-modal">Fermer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
